<template>
  <DefaultCrudView
    :breadcrumbs-items="breadcrumbs"
    :actions="crudActions"
    @create-new="openCreate"
    containerLarge
  >
    <template v-slot:content>
      <v-data-table
        :headers="headers"
        :items="displayAcls"
        :loading="loading"
        :options.sync="options"
        :server-items-length="itemsLength"
        :footer-props="$defaultDataTableFooterProps"
        item-key="user_acl_id"
        class="elevation-1"
      >
        <template v-slot:item.entity="{ item }">
          {{ getEntityTitle(item) }}
        </template>

        <template v-slot:item.acl="{ item }">
          <v-chip
            v-for="chip in getAclChips(item.acl)"
            :key="chip.text"
            class="ma-1"
            small
            color="primary"
            text-color="white"
          >
            {{ chip.text }}
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon class="mr-2" v-bind="attrs" v-on="on" @click="openEdit(item)">mdi-pencil</v-icon>
            </template>
            <span>{{ $t('edit') }}</span>
          </v-tooltip>

          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" @click="requestDelete(item)">mdi-delete</v-icon>
            </template>
            <span>{{ $t('delete') }}</span>
          </v-tooltip>
        </template>
      </v-data-table>

      <DefaultModal :title="dialogTitle" ref="modal" max-width="700px" @submit="saveAcl">
        <template v-slot:content>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-autocomplete
                  v-model="selectedEntities"
                  :items="combinedEntities"
                  :label="$t('group_or_user')"
                  :filter="filterEntities"
                  item-text="display"
                  item-value="key"
                  :multiple="!form.user_acl_id"
                  chips
                  small-chips
                  :readonly="!!form.user_acl_id"
                  :clearable="!form.user_acl_id"
                  outlined
                  :clear-icon="form.user_acl_id ? '' : 'mdi-close-circle'"
                  :attach="false"
                  :menu-props="{ maxHeight: 300, contentClass: 'entity-select-menu' }"
                  :rules="[v => (Array.isArray(v) ? v.length > 0 : !!v) || $t('field_required')]"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-avatar :class="item.type === 'group' ? 'group-bg' : 'avatar'">
                      <v-icon color="secondary">
                        {{ item.type === 'group' ? 'mdi-account-multiple' : 'mdi-account' }}
                      </v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>{{ item.display }}</v-list-item-title>
                      <v-list-item-subtitle v-if="item.email">{{ item.email }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                  <template v-slot:selection="{ item }">
                    <v-chip color="primary" small>
                      <v-avatar left class="avatar">
                        <v-icon color="secondary" small>
                          {{ item.type === 'group' ? 'mdi-account-multiple' : 'mdi-account' }}
                        </v-icon>
                      </v-avatar>
                      {{ item.display }}
                    </v-chip>
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col cols="12">
                <v-label>{{ $t('access_control') }}</v-label>
                <div class="d-flex flex-wrap">
                  <v-checkbox
                    v-for="option in aclOptions"
                    :key="option.value"
                    :label="option.text"
                    :value="option.value"
                    v-model="form.acls"
                    hide-details
                    class="mr-4 mt-2"
                  />
                </div>
              </v-col>
            </v-row>
          </v-container>
        </template>
      </DefaultModal>

      <v-dialog v-model="deleteDialog" max-width="500px">
        <v-card>
          <v-card-title class="headline">{{ $t('confirm_delete') }}</v-card-title>
          <v-card-text>{{ $t('are_you_sure_delete') || $t('confirm_delete') }}</v-card-text>
          <v-card-actions class="justify-end">
            <v-btn text @click="cancelDelete">{{ $t('cancel') }}</v-btn>
            <v-btn color="red" dark @click="confirmDelete">{{ $t('delete') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </DefaultCrudView>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import DefaultCrudView from '@/components/DefaultCrudView.vue'
import DefaultModal from '@/components/DefaultModal.vue'
import UserACLAPI from '@/services/api/job/user_acl'
import UserAPI from '@/services/api/core/user'
import GroupAPI from '@/services/api/core/group'
import UserActions from '@/store/core/user/actions-types'
import GroupActions from '@/store/core/group/actions-types'

const VIEW_LIST_OFF = 1
const VIEW_TIMELINE_OFF = 2

export default {
  name: 'UserAclSettings',

  components: {
    DefaultCrudView,
    DefaultModal
  },

  data() {
    return {
      loading: false,
      acls: [],
      displayAcls: [],
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: [],
      },
      itemsLength: 0,
      userGroups: [],
      users: [],
      selectedEntity: null,
      selectedEntities: [],
      form: {
        user_acl_id: null,
        acls: [],
      },
      deleteDialog: false,
      aclToDelete: null,
    }
  },

  computed: {
    ...mapGetters('app', [
      'getUserApplicationById'
    ]),

    breadcrumbs() {
      return [
        {
          text: this.$t('job_settings'),
          prependIcon: 'mdi-cog-outline',
          iconColor: 'primary',
          to: {
            name: 'JobSettings',
            params: { appId: this.$route.params.appId }
          },
          disabled: false,
          exact: true,
          ripple: true,
        },
        {
          text: this.$t('user_acls'),
          prependIcon: 'mdi-shield-account',
        },
      ]
    },

    crudActions() {
      return [
        {
          text: 'add',
          eventToEmit: 'create-new',
        }
      ]
    },

    appInstance() {
      return this.getUserApplicationById(this.$route.params.appId)
    },

    appConfig() {
      if (!this.appInstance || !this.appInstance.config) return {}
      return this.parseApplicationConfig(this.appInstance.config)
    },

    appTenantId() {
      return (
        this.appConfig.tenant_id ||
        this.appConfig.tenantId ||
        (this.appConfig.tenant && (this.appConfig.tenant.id || this.appConfig.tenant.tenant_id)) ||
        null
      )
    },

    headers() {
      return [
        { text: this.$t('group_user'), value: 'entity' },
        { text: this.$t('access_control'), value: 'acl' },
        { text: this.$t('actions'), value: 'actions', sortable: false, width: '120px' },
      ]
    },

    aclOptions() {
      return [
        { text: this.$t('view_list_off'), value: VIEW_LIST_OFF },
        { text: this.$t('view_timeline_off'), value: VIEW_TIMELINE_OFF },
      ]
    },

    combinedEntities() {
      const groups = this.userGroups.map(g => ({
        key: `group_${g.user_group_id}`,
        display: g.group_title,
        type: 'group',
        id: g.user_group_id,
        email: null,
      }))

      const users = this.users.map(u => ({
        key: `user_${u.users_id}`,
        display: u.name,
        type: 'user',
        id: u.users_id,
        email: u.email,
      }))

      return [...groups, ...users]
    },

    dialogTitle() {
      return this.form.user_acl_id ? this.$t('edit_user_acl') : this.$t('new_user_acl')
    }
  },

  created() {
    this.init()
  },

  watch: {
    options: {
      handler() {
        this.reloadData()
      },
      deep: true,
    }
  },

  methods: {
    ...mapActions('core/user', [
      UserActions.GET_ALL_USERS,
    ]),

    ...mapActions('core/group', [
      GroupActions.GET_ALL_GROUPS,
    ]),

    parseApplicationConfig(string) {
      try {
        return JSON.parse(string)
      } catch (e) {
        return {}
      }
    },

    async init() {
      await this.fetchEntities()
      await this.reloadData()
    },

    async fetchEntities() {
      this.loading = true
      try {
        const usersOptions = {
          'filter[include_foreign]': true,
          'filter[active]': true,
          limit: 9999,
          offset: 0,
        }

        const groupsOptions = {
          'filter[include_foreign]': true,
          'filter[active]': true,
          limit: 9999,
          offset: 0,
        }

        if (this.appTenantId) {
          usersOptions['filter[tenant_id]'] = this.appTenantId
          groupsOptions['filter[tenant]'] = this.appTenantId
        }

        const [groupsResult, usersResult] = await Promise.all([
          this[GroupActions.GET_ALL_GROUPS](groupsOptions),
          this[UserActions.GET_ALL_USERS](usersOptions)
        ])

        const groups = groupsResult.items || []
        this.userGroups = groups.map(g => ({
          user_group_id: g.user_group_id || g.id,
          group_title: g.group_title || g.title || g.name,
        }))

        const users = usersResult.items || []
        this.users = users.map(u => ({
          users_id: u.users_id || u.id,
          name: u.name,
          email: u.email || null,
        }))

      } finally {
        this.loading = false
      }
    },

    async reloadData() {
      if (this.users.length === 0 || this.userGroups.length === 0) {
        await this.fetchEntities()
      }

      const params = { ...this.$route.query }

      if (this.options.sortBy.length > 0) {
        params.order = this.options.sortBy[0]
      }

      if (this.options.sortDesc.length > 0 && this.options.sortDesc[0]) {
        params.direction = 'DESC'
      }

      params.limit = this.options.itemsPerPage
      params.offset = this.options.itemsPerPage * (this.options.page - 1)

      this.loading = true
      try {
        const result = await UserACLAPI.getAllUserAcls(params)
        const data = result.data || {}
        const items = data.items || data || []

        const missingGroupIds = new Set()
        const missingUserIds = new Set()

        items.forEach(item => {
          const groupId = item.user_group_id || item.group_id
          const usersId = item.users_id || item.user_id
          if (groupId && !this.getGroupTitle(groupId) && !item.group_title) {
            missingGroupIds.add(groupId)
          }
          if (usersId && !this.getUserName(usersId) && !item.user_name) {
            missingUserIds.add(usersId)
          }
        })

        if (missingGroupIds.size > 0) {
          const groupFetches = Array.from(missingGroupIds).map(async id => {
            try {
              const res = await GroupAPI.getUserGroup(id)
              const g = res.data || res
              if (g) {
                const user_group_id = g.user_group_id || g.id
                if (!this.userGroups.find(existing => existing.user_group_id === user_group_id)) {
                  this.userGroups.push({
                    user_group_id,
                    group_title: g.group_title || g.title || g.name || '',
                  })
                }
              }
            } catch (e) {
              console.warn('Unable to fetch group', id, e)
            }
          })
          await Promise.all(groupFetches)
        }

        if (missingUserIds.size > 0) {
          const userFetches = Array.from(missingUserIds).map(async id => {
            try {
              const res = await UserAPI.getUser(id)
              const u = res.data || res
              if (u) {
                const users_id = u.users_id || u.id
                if (!this.users.find(existing => existing.users_id === users_id)) {
                  this.users.push({
                    users_id,
                    name: u.name || u.login || u.email || '',
                    email: u.email || null,
                  })
                }
              }
            } catch (e) {
              console.warn('Unable to fetch user', id, e)
            }
          })
          await Promise.all(userFetches)
        }

        this.acls = Array.isArray(items)
          ? items.map(item => {
            const groupId = item.user_group_id || item.group_id
            const usersId = item.users_id || item.user_id
            const groupTitle = groupId ? (this.getGroupTitle(groupId) || item.group_title || '') : ''
            const userName = usersId ? (this.getUserName(usersId) || item.user_name || '') : ''
            return { ...item, group_title: groupTitle, user_name: userName }
          })
          : []
        this.displayAcls = this.aggregateAcls(this.acls)
        this.itemsLength = data.total || (Array.isArray(items) ? items.length : 0)
      } finally {
        this.loading = false
      }
    },

    getAclChips(aclValue) {
      if (!aclValue) return []
      const chips = []
      this.aclOptions.forEach(opt => {
        if (aclValue & opt.value) {
          chips.push({ text: opt.text })
        }
      })
      return chips
    },

    openCreate() {
      this.form = { user_acl_id: null, acls: [] }
      this.selectedEntities = []
      this.$refs.modal.openModal()
    },

    openEdit(item) {
      const usersId = item.users_id || item.user_id || null
      const userGroupId = item.user_group_id || item.group_id || null
      const userAclId = item.primary_user_acl_id || item.user_acl_id || null

      this.form = {
        user_acl_id: userAclId,
        acls: this.aclOptions.filter(opt => (item.acl & opt.value) !== 0).map(opt => opt.value),
      }
      if (userGroupId) {
        this.selectedEntities = `group_${userGroupId}`
      } else if (usersId) {
        this.selectedEntities = `user_${usersId}`
      } else {
        this.selectedEntities = []
      }
      this.$refs.modal.openModal()
    },

    async saveAcl() {
      if (!this.selectedEntities || this.selectedEntities.length === 0) {
        this.$refs.modal.submitting = false
        return
      }
      const targets = Array.isArray(this.selectedEntities) ? this.selectedEntities : [this.selectedEntities]

      try {
        if (this.form.user_acl_id) {
          const [entityType, entityIdRaw] = targets[0].split('_')
          const entityId = parseInt(entityIdRaw, 10)
          const payload = {
            acls: this.form.acls,
          }

          if (entityType === 'group') {
            payload.user_group_id = entityId
            payload.users_id = null
          } else {
            payload.users_id = entityId
            payload.user_group_id = null
          }

          await UserACLAPI.updateUserACL(this.form.user_acl_id, payload)
        } else {
          const createPromises = targets.map(target => {
            const [entityType, entityIdRaw] = target.split('_')
            const entityId = parseInt(entityIdRaw, 10)
            const payload = {
              acls: this.form.acls,
              user_group_id: null,
              users_id: null,
            }

            if (entityType === 'group') {
              payload.user_group_id = entityId
            } else {
              payload.users_id = entityId
            }

            const existing = this.findExistingAclByEntity(entityType, entityId)

            if (existing) {
              const existingBits = this.aclOptions
                .filter(opt => (existing.acl & opt.value) !== 0)
                .map(opt => opt.value)
              const mergedBits = Array.from(new Set([...(existingBits || []), ...this.form.acls]))
              const mergedPayload = {
                ...payload,
                acls: mergedBits
              }

              return UserACLAPI.updateUserACL(existing.user_acl_id, mergedPayload)
            }

            return UserACLAPI.createUserACL(payload)
          })

          await Promise.all(createPromises)
        }

        this.$refs.modal.closeModal()
        await this.reloadData()
      } finally {
        this.$refs.modal.submitting = false
      }
    },

    findExistingAcl(entityType, entityId, aclValue) {
      if (!entityId) return null

      if (entityType === 'group') {
        return this.acls.find(a => (a.user_group_id || a.group_id) == entityId && a.acl == aclValue)
      }

      return this.acls.find(a => (a.users_id || a.user_id) == entityId && a.acl == aclValue)
    },

    findExistingAclByEntity(entityType, entityId) {
      if (!entityId) return null

      if (entityType === 'group') {
        return this.acls.find(a => (a.user_group_id || a.group_id) == entityId)
      }

      return this.acls.find(a => (a.users_id || a.user_id) == entityId)
    },

    computeAclValue(aclsArray) {
      if (!Array.isArray(aclsArray)) return 0
      return aclsArray.reduce((acc, bit) => acc | (parseInt(bit, 10) || 0), 0)
    },

    aggregateAcls(acls) {
      const map = new Map();

      (acls || []).forEach(item => {
        const groupId = item.user_group_id || item.group_id
        const usersId = item.users_id || item.user_id
        const key = groupId ? `group_${groupId}` : (usersId ? `user_${usersId}` : `acl_${item.user_acl_id}`)
        const aclValue = parseInt(item.acl, 10) || 0

        if (!map.has(key)) {
          map.set(key, {
            ...item,
            primary_user_acl_id: item.user_acl_id,
            source_acl_ids: [item.user_acl_id],
            acl: aclValue,
          })
        } else {
          const existing = map.get(key)
          existing.acl = existing.acl | aclValue
          existing.source_acl_ids.push(item.user_acl_id)
        }
      })

      return Array.from(map.values())
    },

    requestDelete(item) {
      this.aclToDelete = item
      this.deleteDialog = true
    },

    cancelDelete() {
      this.deleteDialog = false
      this.aclToDelete = null
    },

    async confirmDelete() {
      if (!this.aclToDelete) {
        this.cancelDelete()
        return
      }

      const userAclId = this.aclToDelete.primary_user_acl_id || this.aclToDelete.user_acl_id

      await UserACLAPI.deleteUserACL(userAclId)
      this.cancelDelete()
      await this.reloadData()
    },

    getEntityTitle(item) {
      const groupId = item.user_group_id || item.group_id
      if (groupId) {
        const title = this.getGroupTitle(groupId) || item.group_title
        return title || ''
      }
      const usersId = item.users_id || item.user_id
      if (usersId) {
        const name = this.getUserName(usersId) || item.user_name
        return name || ''
      }
      return ''
    },

    getUserName(userId) {
      return (this.users.find(u => u.users_id == userId)?.name) || ''
    },

    getGroupTitle(groupId) {
      return (this.userGroups.find(g => g.user_group_id == groupId)?.group_title) || ''
    },

    filterEntities(item, queryText) {
      const display = item.display ? item.display.toLowerCase() : ''
      const email = item.email ? item.email.toLowerCase() : ''
      const searchText = queryText ? queryText.toLowerCase() : ''

      return display.indexOf(searchText) > -1 || (email && email.indexOf(searchText) > -1)
    }
  }
}
</script>

<style scoped>
.avatar {
  background-color: var(--v-primary-base) !important;
}

.group-bg {
  background-color: var(--v-primary-base) !important;
}
</style>

<style>
.entity-select-menu {
  z-index: 9999 !important;
}
</style>
